export const JsonPromptExample = {
  description:
    'A large ancient book lies open on the ground in a ruined city. From its glowing pages, four figures rise: a knight in shining armor, a cloaked wanderer, a trench-coated detective, and a hooded woman. They emerge as if being written into existence.',
  atmosphere: 'Mysterious and cinematic',
  setting:
    'A ruined cityscape with toppled statues and worn stone structures. The massive open book rests at the center, radiating golden light.',
  mood: 'Epic, surreal, and filled with a sense of magical emergence.',
  timeline: [
    {
      time: '0-2s',
      action:
        'The camera slowly approaches the massive open book lying on the rubble-strewn ground, its pages glowing faintly.',
    },
    {
      time: '2-5s',
      action:
        'Ink on the pages begins to swirl and liquefy, forming shimmering ripples of golden light. From this liquid text, humanlike shapes start to rise upward.',
    },
    {
      time: '5-8s',
      action:
        'The figures of a knight, a trench-coated detective, a wanderer, and a hooded woman fully emerge from the glowing book, stepping onto the pages as if born from the words themselves,  illuminated by the glow of the book as particles float in the air.',
    },
  ],
  character_and_object_details: {
    knight:
      "A figure clad in polished, futuristic silver armor with a medieval silhouette, gleaming under the book's light.",
    detective:
      'A man in a long beige trench coat and fedora, with a rugged and thoughtful demeanor.',
    wanderer:
      'A younger figure in a similar trench coat, hands in pockets, embodying resilience.',
    hooded_woman:
      'A cloaked woman in earth-toned robes, with a mysterious and solemn presence.',
    book: 'A massive ancient tome with thick pages, glowing golden inscriptions, and radiating light that brings forth life.',
  },
  type: 'video',
  style:
    'Cinematic fantasy realism with painterly detail and dramatic lighting.',
  quality: 'Ultra HD, high dynamic range, filmic detail.',
  camera:
    '35mm lens with shallow depth of field, positioned at book level for immersive perspective.',
  shot_type: 'Wide establishing shot',
  movement:
    'Slow dolly-in followed by a smooth circular pan around the characters.',
  focus:
    'Deep focus on the book and emerging characters, with blurred ruined city in the background.',
  primary_light:
    'Golden glow emanating from the book’s pages, casting dramatic highlights and soft shadows.',
  effect:
    'Swirling liquid ink transforming into glowing humanoid forms, floating dust motes in the light.',
  negative_prompt: 'cartoon,  modern skyscrapers',
} as const;

export const JsonPromptSchema = {
  type: 'object',
  title: 'Video Shot Json Prompt Schema',
  description:
    'Schema for AI video generation workflows with comprehensive scene specifications',
  properties: {
    description: {
      type: 'string',
      description: 'Main narrative description of the scene',
    },
    atmosphere: {
      type: 'string',
      description: 'Overall emotional and environmental tone',
    },
    setting: {
      type: 'string',
      description: 'Physical location and environmental details',
    },
    mood: {
      type: 'string',
      description: 'Emotional state and psychological atmosphere',
    },
    timeline: {
      type: 'array',
      description: 'Sequence of timed actions and events',
      minItems: 1,
      items: {
        type: 'object',
        required: ['time', 'action'],
        properties: {
          time: {
            type: 'string',
            description: "Time range in seconds (e.g., '0-3s')",
          },
          action: {
            type: 'string',
            description:
              'Detailed description of what happens in this timeframe',
          },
        },
      },
    },
    character_and_object_details: {
      type: 'object',
      description: 'Detailed specifications for characters and key objects',
      minProperties: 1,
    },
    type: {
      type: 'string',
      enum: ['video', 'image', 'audio'],
      description: 'Type of media to generate',
    },
    style: {
      type: 'string',
      description: 'Artistic and visual style specification',
    },
    quality: {
      type: 'string',
      description: 'Technical quality and resolution specifications',
    },
    camera: {
      type: 'string',
      description: 'Camera lens and positioning specifications',
    },
    shot_type: {
      type: 'string',
      description: 'Standard cinematographic shot classification',
    },
    movement: {
      type: 'string',
      description: 'Camera movement specifications',
    },
    focus: {
      type: 'string',
      description: 'Focus and depth of field specifications',
    },
    primary_light: {
      type: 'string',
      description: 'Main lighting setup and quality',
    },
    effect: {
      type: 'string',
      description: 'Special visual effects or atmospheric elements',
    },
    negative_prompt: {
      type: 'string',
      description: 'Elements to avoid or exclude from generation',
    },
  },
  additionalProperties: false,
  examples: [JsonPromptExample],
} as const;
